import { useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import {
  Database as DbIcon,
  Folder,
  File,
  ChevronRight,
  ChevronDown,
  Save,
  Plus,
  Trash2,
  RefreshCcw,
  Search,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function idOf(doc) {
  const id = doc._id;
  if (id && typeof id === "object" && id.$oid) return id.$oid;
  return typeof id === "string" ? id : JSON.stringify(id);
}

export default function Mongo() {
  const [dbs, setDbs] = useState([]);
  const [expandedDbs, setExpandedDbs] = useState(new Set());
  const [collsByDb, setCollsByDb] = useState({});
  const [activeDb, setActiveDb] = useState(null);
  const [activeColl, setActiveColl] = useState(null);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [editorValue, setEditorValue] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  const loadDbs = async () => {
    try {
      const { data } = await api.get("/mongo/databases");
      setDbs(data.databases);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    loadDbs();
  }, []);

  const loadColls = async (dbName) => {
    try {
      const { data } = await api.get(`/mongo/databases/${dbName}/collections`);
      setCollsByDb((c) => ({ ...c, [dbName]: data.collections }));
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const toggleDb = async (dbName) => {
    const next = new Set(expandedDbs);
    if (next.has(dbName)) next.delete(dbName);
    else {
      next.add(dbName);
      if (!collsByDb[dbName]) await loadColls(dbName);
    }
    setExpandedDbs(next);
  };

  const openColl = async (dbName, collName, newSkip = 0, newQuery = query) => {
    setActiveDb(dbName);
    setActiveColl(collName);
    setSkip(newSkip);
    setSelected(null);
    setErr("");
    try {
      const { data } = await api.get(
        `/mongo/databases/${dbName}/collections/${collName}/documents`,
        { params: { skip: newSkip, limit, query: newQuery || undefined } }
      );
      setDocs(data.documents);
      setTotal(data.total);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const editDoc = (doc) => {
    setSelected(doc);
    setIsNew(false);
    setEditorValue(JSON.stringify(doc, null, 2));
  };

  const newDoc = () => {
    setSelected(null);
    setIsNew(true);
    setEditorValue(JSON.stringify({ field: "value" }, null, 2));
  };

  const saveDoc = async () => {
    setErr("");
    setNotice("");
    let parsed;
    try {
      parsed = JSON.parse(editorValue);
    } catch (e) {
      setErr(`JSON-Fehler: ${e.message}`);
      return;
    }
    try {
      if (isNew) {
        await api.post(
          `/mongo/databases/${activeDb}/collections/${activeColl}/documents`,
          { document: parsed }
        );
        setNotice("Dokument angelegt.");
      } else {
        const id = idOf(selected);
        await api.put(
          `/mongo/databases/${activeDb}/collections/${activeColl}/documents/${encodeURIComponent(id)}`,
          { document: parsed }
        );
        setNotice("Dokument aktualisiert.");
      }
      setSelected(null);
      setIsNew(false);
      setEditorValue("");
      openColl(activeDb, activeColl, skip);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const deleteDoc = async (doc) => {
    const id = idOf(doc);
    if (!window.confirm(`Dokument ${id} löschen?`)) return;
    try {
      await api.delete(
        `/mongo/databases/${activeDb}/collections/${activeColl}/documents/${encodeURIComponent(id)}`
      );
      openColl(activeDb, activeColl, skip);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const createColl = async (dbName) => {
    const name = window.prompt("Collection-Name:");
    if (!name) return;
    try {
      await api.post(`/mongo/databases/${dbName}/collections`, { name });
      loadColls(dbName);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const dropColl = async (dbName, collName) => {
    if (!window.confirm(`Collection "${collName}" löschen?`)) return;
    try {
      await api.delete(`/mongo/databases/${dbName}/collections/${collName}`);
      loadColls(dbName);
      if (activeColl === collName && activeDb === dbName) {
        setActiveColl(null);
        setDocs([]);
      }
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const createDb = async () => {
    const name = window.prompt("Neuer Datenbank-Name:");
    if (!name) return;
    try {
      await api.post(`/mongo/databases`, { name, initial_collection: "default" });
      loadDbs();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const dropDb = async (name) => {
    if (!window.confirm(`Datenbank "${name}" unwiderruflich löschen?`)) return;
    try {
      await api.delete(`/mongo/databases/${name}`);
      loadDbs();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div data-testid="mongo-page">
      <PageHeader title="MongoDB" subtitle="// admin" testId="mongo-header">
        <button
          onClick={createDb}
          data-testid="mongo-create-db"
          className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
          Neue DB
        </button>
        <button
          onClick={loadDbs}
          data-testid="mongo-refresh-dbs"
          className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white"
        >
          <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
          Refresh
        </button>
      </PageHeader>

      {err ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#dc2626] border border-[#dc2626]/40 bg-[#dc2626]/5 px-3 py-2">
          {err}
        </div>
      ) : null}
      {notice ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#10b981] border border-[#10b981]/40 bg-[#10b981]/5 px-3 py-2">
          {notice}
        </div>
      ) : null}

      <div className="grid grid-cols-12 gap-px bg-[#1f1f22] mx-8 my-4 min-h-[70vh]">
        {/* DB TREE */}
        <div className="col-span-12 md:col-span-3 bg-[#0a0a0a] overflow-auto max-h-[80vh]">
          <div className="overline px-3 py-3 grid-border-b" style={{ borderBottom: "1px solid #1f1f22" }}>
            // databases
          </div>
          {dbs.map((d) => (
            <div key={d.name}>
              <div className="flex items-center gap-1 px-2 py-1 hover:bg-[#111]">
                <button
                  onClick={() => toggleDb(d.name)}
                  data-testid={`mongo-db-${d.name}`}
                  className="flex-1 flex items-center gap-1 text-[12px] font-mono text-white text-left"
                >
                  {expandedDbs.has(d.name) ? (
                    <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
                  ) : (
                    <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
                  )}
                  <DbIcon className="w-3.5 h-3.5 text-[#22d3ee]" strokeWidth={1.5} />
                  <span className="truncate">{d.name}</span>
                  {d.is_system ? (
                    <span className="ml-auto overline text-[#3f3f46]">sys</span>
                  ) : null}
                </button>
                {!d.is_system ? (
                  <button
                    onClick={() => dropDb(d.name)}
                    data-testid={`mongo-drop-db-${d.name}`}
                    className="text-[#dc2626] hover:text-white"
                    title="Drop DB"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                ) : null}
              </div>
              {expandedDbs.has(d.name) ? (
                <div className="pl-6">
                  <button
                    onClick={() => createColl(d.name)}
                    className="text-[11px] font-mono text-[#8b8d98] hover:text-white px-2 py-1 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" strokeWidth={1.5} />
                    neue collection
                  </button>
                  {(collsByDb[d.name] || []).map((c) => (
                    <div
                      key={c.name}
                      className={[
                        "flex items-center gap-1 hover:bg-[#111] text-[12px] font-mono",
                        activeDb === d.name && activeColl === c.name ? "bg-[#111] text-[#22d3ee]" : "text-[#8b8d98]",
                      ].join(" ")}
                    >
                      <button
                        onClick={() => openColl(d.name, c.name, 0)}
                        data-testid={`mongo-coll-${d.name}-${c.name}`}
                        className="flex-1 flex items-center gap-1 px-2 py-1 text-left"
                      >
                        <File className="w-3 h-3" strokeWidth={1.5} />
                        <span className="truncate">{c.name}</span>
                        <span className="ml-auto text-[10px] text-[#3f3f46]">{c.count ?? ""}</span>
                      </button>
                      <button
                        onClick={() => dropColl(d.name, c.name)}
                        data-testid={`mongo-drop-coll-${d.name}-${c.name}`}
                        className="text-[#dc2626] hover:text-white pr-2"
                      >
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* DOCUMENTS */}
        <div className="col-span-12 md:col-span-5 bg-[#0a0a0a] flex flex-col max-h-[80vh]">
          <div className="px-4 py-2.5 grid-border-b flex items-center gap-2" style={{ borderBottom: "1px solid #1f1f22" }}>
            <span className="overline truncate">
              {activeDb && activeColl ? `${activeDb} / ${activeColl}` : "// keine collection"}
            </span>
            {activeColl ? (
              <>
                <div className="flex-1" />
                <button
                  onClick={newDoc}
                  data-testid="mongo-new-doc"
                  className="px-2 py-1 border border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black text-[10px] font-mono uppercase tracking-[0.15em]"
                >
                  + insert
                </button>
              </>
            ) : null}
          </div>
          {activeColl ? (
            <>
              <div className="px-3 py-2 flex items-center gap-2">
                <div className="flex items-center gap-2 bg-black border border-[#1f1f22] px-2 py-1 flex-1 focus-within:border-[#22d3ee]">
                  <Search className="w-3 h-3 text-[#8b8d98]" strokeWidth={1.5} />
                  <input
                    data-testid="mongo-query-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && openColl(activeDb, activeColl, 0, query)}
                    placeholder='Query JSON z.B. {"field":"value"}'
                    className="bg-transparent outline-none w-full font-mono text-xs text-white placeholder-[#3f3f46]"
                  />
                </div>
                <button
                  onClick={() => openColl(activeDb, activeColl, 0, query)}
                  data-testid="mongo-query-run"
                  className="px-2 py-1 border border-[#1f1f22] text-[#8b8d98] hover:text-white text-[10px] font-mono uppercase tracking-[0.15em]"
                >
                  run
                </button>
              </div>
              <div className="flex-1 overflow-auto divide-y divide-[#1f1f22]">
                {docs.map((d) => (
                  <div
                    key={idOf(d)}
                    data-testid={`mongo-doc-${idOf(d)}`}
                    className="px-3 py-2 hover:bg-[#111] font-mono text-[11px] flex items-start gap-2 group"
                  >
                    <button
                      onClick={() => editDoc(d)}
                      className="flex-1 text-left text-[#8b8d98] hover:text-white"
                    >
                      <div className="text-[#22d3ee] truncate">_id: {idOf(d)}</div>
                      <div className="truncate">{JSON.stringify(d).slice(0, 200)}</div>
                    </button>
                    <button
                      onClick={() => deleteDoc(d)}
                      data-testid={`mongo-delete-doc-${idOf(d)}`}
                      className="text-[#dc2626] hover:text-white"
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
                {!docs.length && (
                  <div className="p-4 text-xs font-mono text-[#8b8d98]">Leere Ergebnismenge.</div>
                )}
              </div>
              <div className="px-3 py-2 grid-border-t flex items-center gap-2 text-[11px] font-mono text-[#8b8d98]" style={{ borderTop: "1px solid #1f1f22" }}>
                <span>
                  {skip + 1}–{Math.min(skip + limit, total)} / {total}
                </span>
                <div className="flex-1" />
                <button
                  onClick={() => openColl(activeDb, activeColl, Math.max(0, skip - limit), query)}
                  disabled={skip === 0}
                  className="px-2 py-1 border border-[#1f1f22] hover:text-white hover:border-white disabled:opacity-30 uppercase tracking-[0.15em]"
                >
                  prev
                </button>
                <button
                  onClick={() => openColl(activeDb, activeColl, skip + limit, query)}
                  disabled={skip + limit >= total}
                  className="px-2 py-1 border border-[#1f1f22] hover:text-white hover:border-white disabled:opacity-30 uppercase tracking-[0.15em]"
                >
                  next
                </button>
              </div>
            </>
          ) : (
            <div className="p-6 text-xs font-mono text-[#8b8d98]">
              Wähle eine Collection aus dem Baum.
            </div>
          )}
        </div>

        {/* JSON EDITOR */}
        <div className="col-span-12 md:col-span-4 bg-[#0a0a0a] flex flex-col max-h-[80vh]">
          <div className="px-4 py-2.5 grid-border-b flex items-center justify-between" style={{ borderBottom: "1px solid #1f1f22" }}>
            <span className="overline truncate">
              {isNew ? "// new document" : selected ? `// edit ${idOf(selected)}` : "// editor"}
            </span>
            {(selected || isNew) && (
              <button
                onClick={saveDoc}
                data-testid="mongo-save-doc"
                className="flex items-center gap-1 px-2 py-1 border border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black text-[10px] font-mono uppercase tracking-[0.15em]"
              >
                <Save className="w-3 h-3" strokeWidth={1.5} />
                speichern
              </button>
            )}
          </div>
          <div className="flex-1">
            {selected || isNew ? (
              <Editor
                theme="vs-dark"
                height="100%"
                language="json"
                value={editorValue}
                onChange={(v) => setEditorValue(v ?? "")}
                options={{
                  fontFamily: "IBM Plex Mono, monospace",
                  fontSize: 12,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                }}
              />
            ) : (
              <div className="p-6 text-xs font-mono text-[#8b8d98]">
                Wähle ein Dokument aus – oder erstelle ein neues mit + insert.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
